import { useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateFund } from "./use-create-fund";

// Sub-bucket 15 — port of /app/admin/mdf/_components/CreateFundDialog.tsx.
// Slug is required + URL-stable (service refuses to update post-create), so
// the form collects it explicitly rather than auto-deriving from name.
// period_start ≤ period_end checked client-side for quick feedback; service
// re-enforces. On success the page navigates to the new fund's detail view.

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function CreateFundDialog({ trigger }: { trigger?: React.ReactNode }) {
  const navigate = useNavigate();
  const mutation = useCreateFund();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [totalEur, setTotalEur] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [notes, setNotes] = useState("");

  function reset() {
    setName("");
    setSlug("");
    setTotalEur("");
    setPeriodStart("");
    setPeriodEnd("");
    setNotes("");
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!SLUG_RE.test(slug)) {
      toast.error("Slug must be lower-case letters, digits, or '-'");
      return;
    }
    const totalParsed = Number(totalEur);
    if (!Number.isInteger(totalParsed) || totalParsed < 0) {
      toast.error("Total must be a whole number of EUR");
      return;
    }
    if (!periodStart || !periodEnd) {
      toast.error("Period start and end are required");
      return;
    }
    if (new Date(periodEnd) < new Date(periodStart)) {
      toast.error("Period end must be on or after period start");
      return;
    }
    mutation.mutate(
      {
        name: name.trim(),
        slug: slug.trim(),
        total_amount_eur: totalParsed,
        period_start: periodStart,
        period_end: periodEnd,
        notes: notes.trim() || undefined,
        status: "open",
      },
      {
        onSuccess: (data) => {
          toast.success("Fund created");
          setOpen(false);
          reset();
          if (data.id) navigate(`/admin/mdf/${data.id}`);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to create fund"));
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Create fund
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Create marketing fund
          </DialogTitle>
          <DialogDescription>
            Vendor-owned budget allocated to partners by manual cap.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-fund-name">Name</Label>
            <Input
              id="mdf-fund-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q3 co-marketing"
              maxLength={200}
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-fund-slug">Slug</Label>
            <Input
              id="mdf-fund-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="q3-co-marketing"
              maxLength={80}
              required
            />
            <p className="text-[11px] text-muted-foreground">
              Lower-case letters, digits, or hyphens. Cannot be changed later.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-fund-total">Total amount (EUR)</Label>
            <Input
              id="mdf-fund-total"
              type="number"
              min={0}
              step={1}
              value={totalEur}
              onChange={(e) => setTotalEur(e.target.value)}
              placeholder="5000"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="mdf-fund-period-start">Period start</Label>
              <Input
                id="mdf-fund-period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mdf-fund-period-end">Period end</Label>
              <Input
                id="mdf-fund-period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-fund-notes">Notes (optional)</Label>
            <Textarea
              id="mdf-fund-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal scope or restrictions…"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create fund
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
